import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import MaskedInput from "react-text-mask";
import { NoteService } from "@services";
import utils from "@utils";
import { Modal, Image } from "../../../../../components";

import "./ViewNotesModal.scss";

class ViewNotesModal extends Component {
	constructor(props) {
		const startDateInMilliSeconds = new Date().getTime() - 30 * 24 * 60 * 60 * 1000;
		let startDate = new Date(startDateInMilliSeconds).toISOString().substr(0, 10);
		let endDate = utils.formatDate(undefined, undefined, undefined, "noslash");
		if (utils.isDesktopSafari || utils.isIE) {
			endDate = endDate.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1");
			startDate = startDate.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1");
		}
		super(props);
		this.initialState = {
			startDate,
			endDate,
			jobEventNotes: [],
			filteredNotes: [],
			notesPagingToken: "",
			hasError: false,
		};
		this.state = { ...this.initialState };
	}

	componentDidUpdate(prevProps) {
		const { show } = this.props;
		if (prevProps.show !== show && show) {
			this.getJobEventNotes();
		}
	}

	/**
	 * Retrieves a list of notes added for the current job.
	 * If more than 25 notes exist, this function is called recursively until all notes are fetched.
	 */
	getJobEventNotes = () => {
		const { lang } = global;
		const { notesPagingToken } = this.state;
		const { jobEventId } = this.props;
		NoteService.getAll({
			displayCount: 25,
			pagingToken: notesPagingToken !== null ? notesPagingToken : null,
			jobEventId,
		})
			.then(({ data }) => {
				this.setState(
					prevState => ({
						jobEventNotes: notesPagingToken
							? [...prevState.jobEventNotes, ...data.ItemList]
							: data.ItemList,
						notesPagingToken: data.PagingToken,
					}),
					() => {
						if (data.PagingToken) {
							this.getJobEventNotes();
						}
						this.displayNotesByDate();
					},
				);
			})
			.catch(() => {
				this.setState({ notesPagingToken: "" });
				utils.notify({
					type: "error",
					message: lang.t("Error getting notes"),
					title: "Error",
				});
			});
	};

	closeModal = () => {
		const { onClose } = this.props;
		this.setState(this.initialState);
		onClose();
	};

	/**
	 * Handles changes to the date range inputs that filter the retrieved notes by date
	 * @returns {(e:Event) => void}
	 */
	handleDateChange = () => e => {
		const { value, name } = e.target;
		// For date inputs on iOS, all '/' characters need to be replaced with '-'
		const formattedValue = utils.isIOSDevice ? value.replace(/\//gi, "-") : value;
		this.setState(
			{
				[name]: formattedValue,
			},
			() => {
				this.areCustomDatesValid();
				if (this.areCustomDatesValid()) {
					this.displayNotesByDate();
				}
			},
		);
	};

	/**
	 * Checks if both date input fields contain valid values based on OS and expected formats
	 * @returns {Boolean} whether both date input values are valid
	 */
	areCustomDatesValid = () => {
		const { startDate, endDate } = this.state;
		// If either date is blank, they are not valid
		if (startDate === "" || endDate === "") {
			this.setState({ hasError: true });
			return false;
		}
		// By default, we will test both dates against a yyyy-mm-dd format
		let dateRegex = /(\d{4})-(\d{2})-(\d{2})/;
		if (utils.isDesktopSafari || utils.isIE) {
			// Check if the entered start date is on or before today
			const today = new Date();
			today.setHours(23, 59, 59, 999);
			const selectedStart = new Date(`${startDate} 00:00`);
			if (selectedStart.getTime() > today.getTime()) return false;
			// We should also make sure the end date is after the start date
			const selectedEnd = new Date(`${endDate} 00:00`);
			if (selectedStart.getTime() > selectedEnd.getTime()) return false;
			// If all previous checks are met on Safari/IE, we should test against a mm/dd/yyyy format
			dateRegex = /(\d{2})\/(\d{2})\/(\d{4})/;
		}
		return dateRegex.test(startDate) && dateRegex.test(endDate);
	};

	/** Sorts retrieved notes by date from most recent to oldest */
	displayNotesByDate = () => {
		const { startDate, endDate, jobEventNotes } = this.state;
		const today = new Date().setHours(23, 59, 59, 999);
		let startDateTime = new Date(`${startDate}T00:00:00`).getTime();
		let ed = new Date(`${endDate}T00:00:00`);
		if (utils.isDesktopSafari || utils.isIE) {
			startDateTime = new Date(`${startDate} 00:00`).getTime();
			ed = new Date(`${endDate} 00:00`);
		}
		const endDateTime = ed.setHours(23, 59, 59, 999);
		if (
			startDateTime > endDateTime ||
			startDateTime > today ||
			endDateTime > today ||
			Number.isNaN(startDateTime) ||
			Number.isNaN(endDateTime)
		) {
			this.setState({ hasError: true });
			return;
		}
		const filteredNotes = jobEventNotes.filter(
			note =>
				new Date(note?.CreateDateUTC).getTime() <= endDateTime &&
				new Date(note?.CreateDateUTC).getTime() >= startDateTime,
		);
		this.setState({ filteredNotes, hasError: false });
	};

	render() {
		const { lang } = global;
		const { show, timeZoneId, jobLocation, user } = this.props;
		const { filteredNotes, hasError, startDate, endDate } = this.state;
		const dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
		const todaysDate = utils.formatDate(undefined, undefined, undefined, "noslash");

		return (
			<Modal
				id="view-notes-modal"
				dialogClassName="view-notes-dialog"
				size="lg"
				show={show}
				onHide={() => this.closeModal()}
			>
				<Modal.Header
					closeButton
					className="hidden-print"
				>
					<Modal.Title>{`Notes for ${jobLocation}`}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="container-fluid">
					<div className="row date-row hidden-print">
						<div
							className={classNames("col-xs-6 col-md-offset-3 col-md-3", {
								"has-error": hasError,
							})}
						>
							<label
								htmlFor="startDate"
								className="date-label"
							>
								{lang.t("Start Date")}
							</label>
							<Choose>
								<When condition={utils.isDesktopSafari || utils.isIE}>
									<MaskedInput
										name="startDate"
										id="startDate"
										className="form-control"
										type="text"
										max={todaysDate}
										mask={dateMask}
										placeholder="mm/dd/yyyy"
										value={startDate}
										onChange={this.handleDateChange()}
									/>
								</When>
								<Otherwise>
									<input
										name="startDate"
										id="startDate"
										className="form-control"
										type="date"
										max={todaysDate}
										value={startDate}
										onChange={this.handleDateChange()}
									/>
								</Otherwise>
							</Choose>
						</div>
						<div
							className={classNames("col-xs-6 col-md-3", {
								"has-error": hasError,
							})}
						>
							<label
								htmlFor="endDate"
								className="date-label"
							>
								{lang.t("End Date")}
							</label>
							<Choose>
								<When condition={utils.isDesktopSafari || utils.isIE}>
									<MaskedInput
										name="endDate"
										id="endDate"
										className="form-control"
										type="text"
										max={todaysDate}
										mask={dateMask}
										placeholder="mm/dd/yyyy"
										value={endDate}
										onChange={this.handleDateChange()}
									/>
								</When>
								<Otherwise>
									<input
										name="endDate"
										id="endDate"
										className="form-control"
										type="date"
										max={todaysDate}
										value={endDate}
										onChange={this.handleDateChange()}
									/>
								</Otherwise>
							</Choose>
						</div>
					</div>
					<div className="view-notes-table">
						<Choose>
							<When condition={filteredNotes.length === 0}>
								<div className="text-center text-muted">
									{lang.t("No notes found in this date range")}
								</div>
							</When>
							<Otherwise>
								<h2 className="visible-print">{jobLocation}</h2>
								<h4 className="visible-print">
									{`${lang.t("Notes")} ${lang.t("From")}:
									${utils.formatDate(new Date(`${startDate} 00:00`), "en-US", undefined, "numeric")}`}
									&nbsp;&nbsp;
									{`${lang.t("To")}: ${utils.formatDate(new Date(`${endDate} 00:00`), "en-US", undefined, "numeric")}`}
								</h4>
								<table className="table table-responsive">
									<thead>
										<tr>
											<th className="created">{lang.t("Created By")}</th>
											<th className="job">{lang.t("Job")}</th>
											<th className="comments">{lang.t("Comments")}</th>
										</tr>
									</thead>
									<tbody>
										<For
											each="note"
											of={filteredNotes}
											index="index"
										>
											<tr key={`note_${note.Id}`}>
												<td>
													{note.UserName}
													<br />
													{utils.formatDate(
														new Date(`${note.CreateDateUTC}Z`),
														"en-US",
														timeZoneId,
														"2-digit",
													)}
													&nbsp;
													{utils.formatTime(
														new Date(`${note.CreateDateUTC}Z`),
														user.UserPreferences !== "useMilitaryTime",
														timeZoneId,
													)}
												</td>
												<td>{note.JobEventName ?? ""}</td>
												<td>
													<p>{note.Comment}</p>
													<If condition={note.Url}>
														<a
															href={note.Url}
															aria-label="view notes image"
															target="_blank"
															rel="noopener noreferrer"
														>
															<Image
																src={note.Url}
																fallbackSrc="default"
																alt="note image"
																className="img-responsive note-image"
															/>
														</a>
													</If>
												</td>
											</tr>
										</For>
									</tbody>
								</table>
							</Otherwise>
						</Choose>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="row">
						<div
							className={`${filteredNotes.length > 0 ? "col-sm-2 col-xs-5 col-sm-offset-4 col-xs-offset-1" : ""}`}
						>
							<button
								className="btn btn-link hidden-print btn-block"
								type="button"
								onClick={() => this.closeModal()}
							>
								{lang.t("Close")}
							</button>
						</div>
						<If condition={filteredNotes.length > 0 ? "col-sm-2 col-xs-6" : ""}>
							<div className="col-sm-2 col-xs-5">
								<button
									id="button-print-table"
									className="btn btn-primary hidden-print btn-block"
									type="button"
									onClick={() => window.print()}
								>
									{lang.t("Print")}
								</button>
							</div>
						</If>
					</div>
				</Modal.Footer>
			</Modal>
		);
	}
}

ViewNotesModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	timeZoneId: PropTypes.string.isRequired,
	user: PropTypes.any.isRequired,
	jobLocation: PropTypes.string,
	jobEventId: PropTypes.number,
};

ViewNotesModal.defaultProps = {
	jobLocation: undefined,
	jobEventId: undefined,
};

export default ViewNotesModal;
